import mixpanel from 'mixpanel-browser';
import { datadogLogs } from '@datadog/browser-logs';

import { getUrlParams } from './utils';
import { getRefValue, getSessionID } from 'services/storage';

import type { MixpanelEventMap } from './mixpanelEvents';

/**
 * Datadog's signature is `warn(message, messageContext?, error?)` — the error has to be the third
 * argument. Putting it in the context object serializes it to `{}`, because `Error`'s own
 * properties are non-enumerable, which silently drops the message and stack.
 *
 * A non-`Error` throw (a string, a rejected value) goes in the context as `thrown` instead of
 * being cast, so nothing is lost either way.
 *
 * Message format matches /admin, so one Datadog query covers both apps.
 */
function logMixpanelError(
  action: string,
  contextData: Record<string, unknown>,
  error: unknown
): void {
  const isError = error instanceof Error;

  datadogLogs.logger.warn(
    `[mixpanel] ${action} failed`,
    isError ? contextData : { ...contextData, thrown: error },
    isError ? error : undefined
  );
}

export const mixpanelClient = {
  /**
   * Track an event. Errors are caught and logged to Datadog.
   *
   * Generic over the event catalog, so both the name and its property shape are checked here —
   * an event can't be fired unless it's declared in `MixpanelEventMap`, and its properties must
   * match that declaration. There is no untyped path for *events*.
   *
   * Super properties are a separate channel: `register` and `setAuthState` reach Mixpanel without
   * going through the map, so `is_authenticated` and the Statsig experiment groups are not checked
   * against it.
   *
   * Ambient context is injected here and read live on every call. `properties` is spread **last**,
   * so a call site can override an injected value — see `Donate Again Button Clicked`.
   *
   * Still prefer `trackEvent` or a named helper from `./mixpanelEvents` at call sites: the
   * helpers own the shared derivation, so going direct duplicates it. The import above is
   * type-only, so there's no runtime cycle.
   */
  track<Name extends keyof MixpanelEventMap>(
    eventName: Name,
    properties: MixpanelEventMap[Name] | null
  ): void {
    try {
      const { urlPartner, urlRef } = getUrlParams();

      mixpanel.track(eventName, {
        session_id: getSessionID(),
        ref_code: urlRef || getRefValue() || undefined,
        query_string: urlPartner,
        ...(properties || {}),
      });
    } catch (error) {
      logMixpanelError('track', { eventName }, error);
    }
  },

  /**
   * Registers `is_authenticated` as a Mixpanel super property, so it merges into every event.
   *
   * A snapshot rather than a live read like the other ambient context, because auth state changes
   * only on login/logout — unlike `session_id` / `ref_code` / `query_string`, which change per
   * navigation and would go stale if registered.
   *
   * The catch is not defensive: this is called from app root, which sits outside
   * every error boundary in site, so any uncaught throw here unmounts the whole app rather than
   * one screen. Mixpanel guards its own storage access, so the blocked-storage path specifically
   * is already covered. This catches everything else.
   */
  setAuthState(isAuthenticated: boolean): void {
    try {
      mixpanel.register({ is_authenticated: isAuthenticated });
    } catch (error) {
      logMixpanelError('setAuthState', { isAuthenticated }, error);
    }
  },

  /**
   * Identify a user. Errors are caught and logged to Datadog.
   */
  identify(uniqueId: string): void {
    try {
      mixpanel.identify(uniqueId);
    } catch (error) {
      logMixpanelError('identify', { uniqueId }, error);
    }
  },

  /**
   * Identify user and send a post-identify event so Mixpanel can merge identities.
   */
  identifyWithMergeEvent(uniqueId: string): void {
    try {
      mixpanel.identify(uniqueId);
      this.track('User Identified', { user_id: uniqueId });
    } catch (error) {
      logMixpanelError('identifyWithMergeEvent', { uniqueId }, error);
    }
  },

  register(data: Record<string, string | number | null>): void {
    try {
      mixpanel.register(data);
    } catch (error) {
      logMixpanelError('register', { keys: Object.keys(data) }, error);
    }
  },

  /**
   * Reset the mixpanel state (e.g. on logout). Errors are caught and logged to Datadog.
   */
  reset(): void {
    try {
      mixpanel.reset();
      // reset() clears all super properties, and its only call site is logout — so re-register
      // the one fact that is definitionally true afterwards. Without this, events between logout
      // and the next auth-state change carry no `is_authenticated` at all.
      mixpanel.register({ is_authenticated: false });
    } catch (error) {
      logMixpanelError('reset', {}, error);
    }
  },
};
